import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { ShoppingBag, Menu, X, UserRound } from "lucide-react";
import { useCart } from "../lib/cart";
import { useMemberAuth } from "../lib/memberAuth";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "./ui/sheet";

const NAV = [
  { to: "/membership", label: "Membership" },
  { to: "/restaurant", label: "Restaurant" },
  { to: "/bar", label: "Bar" },
  { to: "/cigar-lounge", label: "Cigar Lounge" },
  { to: "/private-spaces", label: "Private Spaces" },
  { to: "/events", label: "Events" },
  { to: "/shop", label: "Shop" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { totals } = useCart();
  const { account } = useMemberAuth();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "nav-glass" : "bg-transparent"}`}
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-home-link" className="shrink-0">
          <Logo variant="horizontal" />
        </Link>

        <nav className="hidden xl:flex items-center gap-7" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `font-sans text-[11px] tracking-[0.28em] uppercase transition-colors duration-300 ${isActive ? "text-[var(--twr-bronze)]" : "text-[var(--twr-warm-white)]/85 hover:text-[var(--twr-bronze)]"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/reservations"
            data-testid="nav-reserve-cta"
            className="hidden lg:inline-flex font-sans text-[11px] tracking-[0.28em] uppercase border border-[var(--twr-bronze)] text-[var(--twr-bronze)] px-4 py-2.5 hover:bg-[var(--twr-bronze)] hover:text-[#0A0A0A] transition-colors duration-300"
          >
            Reserve
          </Link>
          <Link
            to={account ? "/account" : "/account/login"}
            data-testid="nav-member-login"
            title={account ? "Your Account" : "Sign In"}
            aria-label={account ? "Your Account" : "Sign In"}
            className={`relative inline-flex items-center justify-center w-10 h-10 border transition-colors ${account ? "border-[var(--twr-bronze)]/60 text-[var(--twr-bronze)]" : "border-transparent text-[var(--twr-warm-white)]"} hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)]`}
          >
            <UserRound size={18} strokeWidth={1.4} />
          </Link>
          <Link
            to="/cart"
            data-testid="nav-cart-link"
            className="relative inline-flex items-center justify-center w-10 h-10 border border-transparent hover:border-[var(--twr-bronze)] text-[var(--twr-warm-white)] hover:text-[var(--twr-bronze)] transition-colors"
            aria-label="Cart"
          >
            <ShoppingBag size={18} strokeWidth={1.4} />
            {totals.count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-[var(--twr-bronze)] text-[#0A0A0A] text-[10px] font-semibold rounded-full w-4 h-4 inline-flex items-center justify-center">
                {totals.count}
              </span>
            )}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-menu-btn" className="xl:hidden inline-flex items-center justify-center w-10 h-10 text-[var(--twr-warm-white)]" aria-label="Open menu">
                <Menu size={20} strokeWidth={1.4} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0A0A0A] border-l border-[var(--twr-line)] text-[var(--twr-warm-white)] w-[88vw] sm:w-[420px]">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <Logo variant="horizontal" />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-10 flex flex-col gap-2">
                {NAV.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="font-serif text-2xl tracking-tight py-2 border-b border-[var(--twr-line)] hover:text-[var(--twr-bronze)] transition-colors"
                  >
                    {n.label}
                  </Link>
                ))}
                <Link to="/reservations" data-testid="mobile-reserve-cta" className="btn-bronze mt-8">Reserve a Table</Link>
                <Link to="/membership/apply" data-testid="mobile-apply-cta" className="btn-ghost mt-3">Apply for Membership</Link>
                <Link to={account ? "/account" : "/account/login"} data-testid="mobile-member-login" className="btn-ghost mt-3 inline-flex items-center justify-center gap-2">
                  <UserRound size={14} strokeWidth={1.5} /> {account ? "Your Account" : "Sign In / Create Account"}
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
